import { OverlayContainer } from '@angular/cdk/overlay';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners
} from '@angular/core';
import { MAT_ICON_DEFAULT_OPTIONS, MatIconRegistry } from '@angular/material/icon';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import {
  CUSTOM_ELEMENT,
  DeightonCustomOverlay,
  DeightonUserProvider,
  TranslocoInlineLoader,
  translocoConf
} from '@deighton/shared-ui/services';
import { authInterceptor } from '../services/auth.interceptor';
import { routes } from './app.routes';

/**
 * Global Angular application configuration.
 *
 * Authentication in standalone mode is handled by LicensingAuthService, which
 * implements the same backend-mediated PKCE flow as the shell-app. The OktaAuthModule
 * is no longer required here.
 *
 * When hosted as an MFE inside the shell-app, the shell's own auth interceptors and
 * DI providers take effect; this configuration is not used.
 *
 * Apollo GraphQL is configured at the route level in app.routes.ts.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([authInterceptor])),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    importProvidersFrom(BrowserAnimationsModule),

    provideTransloco({
      config: translocoConf,
      loader: TranslocoInlineLoader
    }),

    DeightonUserProvider,

    // Redirect CDK overlays (tooltips, menus, dialogs) into d-style-wrapper for Material theming.
    { provide: CUSTOM_ELEMENT, useValue: 'd-style-wrapper' },
    { provide: OverlayContainer, useClass: DeightonCustomOverlay },

    // Set default font set for mat-icon to Material Symbols Outlined.
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' }
    },

    // Register material-symbols-outlined as a ligature font so fontIcon input renders icons correctly.
    {
      provide: APP_INITIALIZER,
      useFactory:
        (iconRegistry: MatIconRegistry): (() => void) =>
        (): void => {
          iconRegistry.registerFontClassAlias(
            'material-symbols-outlined',
            'material-symbols-outlined mat-ligature-font'
          );
        },
      deps: [MatIconRegistry],
      multi: true
    }
  ]
};
